import React, { useState, useEffect, useRef } from 'react';
import CommonModalLayout from '../Layouts/CommonModalLayout';
import { buttonStyles, inputStyles } from '../../styles/commonStyles';
import { useTranslation } from 'react-i18next';
import { createCollection } from '../../api/collections';
import { useDispatch, useSelector } from 'react-redux';
import { setChannelCreated } from '../../redux/actions';
import { useQueryClient } from 'react-query';
import ErrorMessage from '../Common/ErrorMessage';

const MIN_CHANNEL_NAME_LENGTH = 4;

const CreateChannelModal = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [channelName, setChannelName] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const userId = useSelector(state => state.auth.user?.id);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setChannelName('');
            setDescription('');
            setError('');
            setTimeout(() => {
                inputRef.current && inputRef.current.focus();
            }, 0);
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && document.activeElement === inputRef.current) {
                handleCreate();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, channelName]);

    const handleCreate = async () => {
        setError('');
        if (!channelName || channelName.length < MIN_CHANNEL_NAME_LENGTH) {
            setError(t('channel.name_min_length', { min: MIN_CHANNEL_NAME_LENGTH }) || `Минимальная длина названия — ${MIN_CHANNEL_NAME_LENGTH} символа`);
            return;
        }
        try {
            await createCollection({ name: channelName, description });
            queryClient.invalidateQueries(['collections', userId]);
            dispatch(setChannelCreated(true));
            handleClose();
        } catch (error) {
            if (error?.response?.status === 422) {
                setError(t('channel.name_min_length', { min: MIN_CHANNEL_NAME_LENGTH }) || `Минимальная длина названия — ${MIN_CHANNEL_NAME_LENGTH} символа`);
            } else {
                setError(error.message || 'Ошибка создания канала');
            }
        }
    };

    const handleClose = () => {
        setChannelName('');
        setDescription('');
        setError('');
        onClose();
    };

    return (
        <CommonModalLayout
            isOpen={isOpen}
            onClose={handleClose}
            title={t('channel.create')}
        >
            <div className="space-y-4">
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('channel.name')}
                    </label>
                    <input
                        type="text"
                        value={channelName}
                        onChange={(e) => setChannelName(e.target.value)}
                        className={inputStyles}
                        placeholder={t('channel.name_placeholder')}
                        ref={inputRef}
                    />
                </div>
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('channel.description') || 'Описание канала'}
                    </label>
                    <textarea
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        className={inputStyles + ' min-h-[80px]'}
                        placeholder={t('channel.description_placeholder') || 'Описание, ссылки, контакты, QR...'}
                    />
                </div>
                <ErrorMessage message={error} />
                <div className="flex justify-end space-x-2">
                    <button
                        onClick={handleClose}
                        className={buttonStyles}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        onClick={handleCreate}
                        className={buttonStyles}
                    >
                        {t('common.create')}
                    </button>
                </div>
            </div>
        </CommonModalLayout>
    );
};

export default CreateChannelModal;
